import React, { useState } from 'react';

import dayjs from 'dayjs';

import { ReactComponent as EyeDisabledIcon } from '@pkg/assets/icons/v2/eye-off.svg';
import { ReactComponent as EyeIcon } from '@pkg/assets/icons/v2/eye.svg';
import { Preview, Wrapper } from '@pkg/components';
import { Section } from '@pkg/components/MarkdownEditor/styles';
import { displayFormatNow } from '@pkg/components/Sections/Admin';
import { CriteriaTable, DataDisplay, DataDisplayTable } from '@pkg/components/Sections/Admin/Fantasy/GameData/styles';
import { PrizeItem } from '@pkg/components/Sections/Admin/Fantasy/Prizes';
import { howToPlay } from '@pkg/components/Sections/Admin/Fantasy/Rules/base-faq-md-string';
import { Button } from '@pkg/components/Sections/Profile/Buttons';

import { type Competition } from '@app/client/common/structs';
import { splitCamelCase } from '@app/helpers';
import { Rule } from '@app/pages/FantasyWishlist/sections/AboutSection/components/Rule/Rule';
import { RulesGrid } from '@app/pages/FantasyWishlist/sections/AboutSection/styles';

export const GameData = ({ gameData }: { gameData?: Competition }) => {
    const [showPreview, setShowPreview] = useState(true);
    const [showFaq, setShowFaq] = useState(true);
    if (!gameData) return null;

    const prizeMarkup = (
        <DataDisplayTable>
            <DataDisplay $isTitle>Player prizes</DataDisplay>
            {gameData?.prizes?.length ? (
                <DataDisplay>
                    {gameData.prizes.map((p) => (
                        <PrizeItem key={`${p.name}-${p.rank}`} prize={p} compact />
                    ))}
                </DataDisplay>
            ) : (
                <DataDisplay>No prizes set</DataDisplay>
            )}
        </DataDisplayTable>
    );

    const teamPrizeMarkup = (
        <DataDisplayTable>
            <DataDisplay $isTitle>Team prizes</DataDisplay>
            {gameData?.team_prizes?.length ? (
                <DataDisplay>
                    {gameData.team_prizes?.map((p) => <PrizeItem key={p.name} prize={p} compact />)}
                </DataDisplay>
            ) : (
                <DataDisplay>No team prizes set</DataDisplay>
            )}
        </DataDisplayTable>
    );

    const faqMarkup = (
        <DataDisplayTable>
            <DataDisplay $isTitle>FAQ</DataDisplay>
            <DataDisplay>
                <Wrapper>
                    <Button color="clear" size="xsmall" onClick={() => setShowFaq((c) => !c)}>
                        {showFaq ? <EyeDisabledIcon /> : <EyeIcon />}
                    </Button>
                    {showFaq && (
                        <>
                            <Rule title={howToPlay.question} content={howToPlay.answers} key={'how-to-play'} />
                            {gameData?.questions_and_answers?.length ? (
                                <RulesGrid>
                                    {gameData.questions_and_answers.map((rule, index) => (
                                        <Rule title={rule.question} content={rule.answers} key={index} />
                                    ))}
                                </RulesGrid>
                            ) : (
                                <DataDisplay>
                                    <Button to="faq">Add FAQ</Button>
                                </DataDisplay>
                            )}
                        </>
                    )}
                </Wrapper>
            </DataDisplay>
        </DataDisplayTable>
    );

    const valueMarkup = (
        <DataDisplayTable>
            <DataDisplay $isTitle>Values criteria</DataDisplay>
            <DataDisplay>
                <CriteriaTable>
                    <span>Max value:</span>
                    <strong>{gameData.max_value}</strong>
                </CriteriaTable>
                <CriteriaTable>
                    <span>Max assets:</span>
                    <strong>{gameData.max_assets}</strong>
                </CriteriaTable>
                <CriteriaTable>
                    <span>Max player count:</span>
                    <strong>{gameData.max_player_count}</strong>
                </CriteriaTable>
            </DataDisplay>
        </DataDisplayTable>
    );

    const dateMarkup = (
        <DataDisplayTable>
            <DataDisplay $isTitle>
                <div>
                    Game Dates
                    <br />
                    <span>[Local time]</span>
                </div>
            </DataDisplay>
            <DataDisplay>
                <CriteriaTable>
                    <span>Submissions start:</span>
                    <strong>{dayjs(gameData.submission_starts_at).format(displayFormatNow)}</strong>
                </CriteriaTable>
                <CriteriaTable>
                    <span>Last joining time:</span>
                    <strong>{dayjs(gameData.submission_ends_at).format(displayFormatNow)}</strong>
                </CriteriaTable>
                <CriteriaTable>
                    <span>Game ends:</span>
                    <strong>{dayjs(gameData.ends_at).format(displayFormatNow)}</strong>
                </CriteriaTable>
            </DataDisplay>
        </DataDisplayTable>
    );

    const dynamicCriteria = (
        <DataDisplayTable>
            <DataDisplay $isTitle>
                <div>
                    Dynamic criteria
                    <br />
                    <span>[minimum values]</span>
                </div>
            </DataDisplay>
            <DataDisplay>
                {gameData.dynamic_criteria?.map((criteria) =>
                    Object.keys(criteria).map((criteriaKey) => {
                        const criteriaValues = criteria[criteriaKey];
                        return (
                            <CriteriaTable key={criteriaKey}>
                                <span>
                                    {splitCamelCase(criteriaKey).replace('Minimum', '').replace('Percentage', '%')}
                                </span>
                                <CriteriaTable>
                                    {Object.keys(criteriaValues).map((valueKey) => (
                                        <div key={`${criteriaKey}-${valueKey}`}>
                                            {valueKey.replaceAll('_', ' ')}: <strong>{criteriaValues[valueKey]}</strong>
                                        </div>
                                    ))}
                                </CriteriaTable>
                            </CriteriaTable>
                        );
                    }),
                )}
            </DataDisplay>
        </DataDisplayTable>
    );

    const defaultRules = `[No rules set - add now](/admin/fantasy/${gameData.slug}/rules)`;
    const rulesMarkup = (
        <DataDisplayTable>
            <DataDisplay $isTitle>Rules</DataDisplay>
            <DataDisplay>
                <Wrapper justifyContent="flex-end">
                    <Button color="clear" size="xsmall" onClick={() => setShowPreview((c) => !c)}>
                        {showPreview ? <EyeDisabledIcon /> : <EyeIcon />}
                    </Button>
                    <Section $hidden={!showPreview}>
                        <Preview
                            markdown={gameData.rules || defaultRules}
                            linkTarget={gameData?.rules ? '_blank' : '_self'}
                        />
                    </Section>
                </Wrapper>
            </DataDisplay>
        </DataDisplayTable>
    );

    return (
        <Wrapper>
            {dateMarkup}
            {valueMarkup}
            {prizeMarkup}
            {teamPrizeMarkup}
            {dynamicCriteria}
            {rulesMarkup}
            {faqMarkup}
        </Wrapper>
    );
};
